import { memo, ReactNode, Fragment } from 'react'
import { Handle, Position } from '@xyflow/react'
import { Box, Typography, Chip, Tooltip } from '@mui/material'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import { WorkflowNodeData, useWorkflowStore } from '@store/workflowStore'
import {
  WorkflowNodeType,
  WorkflowConditionType,
  getNodeTypeColor,
  getNodeTypeLabel,
  getNodeTypeAbbreviation,
} from '@features/adventures/types/workflow.types'

// Challenge condition types - user must actively do something
const CHALLENGE_CONDITION_TYPES = [
  WorkflowConditionType.NumericInput,
  WorkflowConditionType.QuestionAnswer,
  WorkflowConditionType.QRCode,
  WorkflowConditionType.ModeratorApproval,
]

interface BaseNodeProps {
  id: string
  data: WorkflowNodeData
  selected?: boolean
  icon: ReactNode
  showSourceHandle?: boolean
  showTargetHandle?: boolean
  sourceHandleCount?: number
  sourceHandleLabels?: string[] // Labels for multiple source handles (e.g., accepted numpad codes)
  showCancelHandle?: boolean // Show a cancel handle for nodes like numpad
  showSkipHandle?: boolean // Show a skip handle for Q/A nodes
  showFailedHandle?: boolean // Show a failed handle for numpad nodes (max attempts exceeded)
  showIncorrectHandle?: boolean // Show an incorrect handle for Q/A nodes (wrong answer)
  headerLabel?: string // Custom header label override
  hideConditionChip?: boolean // Hide the conditions count chip
  /** Optional extra content rendered inside the node body (e.g. RouteNode chips). */
  children?: ReactNode
}

function BaseNode({
  id,
  data,
  selected,
  icon,
  showSourceHandle = true,
  showTargetHandle = true,
  sourceHandleCount = 1,
  sourceHandleLabels,
  showCancelHandle = false,
  showSkipHandle = false,
  showFailedHandle = false,
  showIncorrectHandle = false,
  headerLabel,
  hideConditionChip = false,
  children,
}: BaseNodeProps) {
  // Get validation state for this node
  const getNodeValidationState = useWorkflowStore(state => state.getNodeValidationState)
  const validationState = getNodeValidationState(id)

  // Determine if this is a Logical Conditions node (Decision node without challenge conditions/configs)
  const hasChallengeConfig = !!(data.numpadConfig || data.qrCodeConfig || data.questionAnswerConfig || data.moderatorApprovalConfig)
  const hasChallengeCondition = data.conditions?.some(c => CHALLENGE_CONDITION_TYPES.includes(c.conditionType)) ?? false
  const isLogicalCondition = data.nodeType === WorkflowNodeType.Decision && !hasChallengeConfig && !hasChallengeCondition

  // Determine border color based on validation state
  const baseBorderColor = getNodeTypeColor(data.nodeType, isLogicalCondition)
  let borderColor = baseBorderColor
  let borderWidth = 2

  if (validationState?.hasErrors) {
    borderColor = '#f44336' // error red
    borderWidth = 3
  } else if (validationState?.hasWarnings) {
    borderColor = '#ff9800' // warning orange
    borderWidth = 3
  }

  const typeLabel = headerLabel || getNodeTypeLabel(data.nodeType)

  // Generate sequence ID prefix (e.g., "L1", "C2", "D3")
  const sequenceId = data.sequenceNumber
    ? `${getNodeTypeAbbreviation(data.nodeType)}${data.sequenceNumber}`
    : null

  // Calculate minimum height for nodes with multiple handles
  // Each handle needs ~30px of space, plus cancel/skip/failed handles at top
  const numpadHandleCount = sourceHandleLabels?.length ?? 0
  const isQuestionAnswerNode = !!data.questionAnswerConfig
  let minHeightForHandles: number | undefined

  if (isQuestionAnswerNode) {
    // Q/A nodes need space for: cancel (55px), optional skip (80px), correct, incorrect + margin
    if (showIncorrectHandle && showSkipHandle) {
      minHeightForHandles = 170 // cancel + skip + correct + incorrect
    } else if (showIncorrectHandle) {
      minHeightForHandles = 145 // cancel + correct + incorrect
    } else if (showSkipHandle) {
      minHeightForHandles = 145 // cancel + skip + correct
    } else {
      minHeightForHandles = 120 // cancel + correct
    }
  } else if (showCancelHandle && numpadHandleCount > 0) {
    // Numpad nodes need space for: cancel (55px), optional failed (80px), and code handles
    const topHandlesOffset = showFailedHandle ? 105 : 85
    minHeightForHandles = Math.max(showFailedHandle ? 175 : 155, topHandlesOffset + numpadHandleCount * 30)
  } else if (!showCancelHandle && sourceHandleLabels && sourceHandleLabels.length > 1) {
    // Labeled-only nodes (e.g. ModeratorApproval) need space for all handles below header
    minHeightForHandles = Math.max(160, 70 + sourceHandleLabels.length * 30)
  }

  // Calculate handle positions for numpad codes using pixel offsets
  // Cancel handle is at 45px, failed handle (if shown) at 70px
  // All handles start below the header bar (~40px)
  const getCodeHandlePosition = (index: number, total: number, nodeHeight: number): number => {
    if (total === 1) return nodeHeight * 0.6
    const startPx = showFailedHandle ? 105 : 80  // Start below cancel/failed handles
    const endPx = nodeHeight - 15  // Leave some bottom margin
    const availableSpace = endPx - startPx
    const step = total > 1 ? availableSpace / (total - 1) : 0
    return startPx + (step * index)
  }

  // Actual height for positioning (use minHeight or default)
  const actualHeight = minHeightForHandles ?? 100

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: 180,
        maxWidth: 250,
        minHeight: minHeightForHandles,
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: `${borderWidth}px solid ${borderColor}`,
        boxShadow: selected ? `0 0 0 2px ${borderColor}` : 1,
        transition: 'box-shadow 0.2s, border-color 0.2s, border-width 0.2s',
      }}
    >
      {/* Validation error/warning badge */}
      {validationState && (validationState.hasErrors || validationState.hasWarnings) && (
        <Tooltip
          title={`${validationState.errorCount} error${validationState.errorCount !== 1 ? 's' : ''}, ${validationState.warningCount} warning${validationState.warningCount !== 1 ? 's' : ''}`}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: validationState.hasErrors ? 'error.main' : 'warning.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 20,
              boxShadow: 2,
            }}
          >
            {validationState.hasErrors ? (
              <ErrorIcon sx={{ fontSize: 14 }} />
            ) : (
              <WarningIcon sx={{ fontSize: 14 }} />
            )}
          </Box>
        </Tooltip>
      )}
      {/* Target Handle (left) - flow connections */}
      {showTargetHandle && (
        <Handle
          id="flow-target"
          type="target"
          position={Position.Left}
          isConnectable={true}
          style={{
            background: borderColor,
            width: 16,
            height: 16,
            border: '2px solid white',
            zIndex: 50,
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          backgroundColor: baseBorderColor,
          color: 'white',
        }}
      >
        {icon}
        <Typography variant="caption" fontWeight="bold">
          {sequenceId ? `${sequenceId} - ${typeLabel}` : typeLabel}
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ p: 1.5 }}>
        <Typography
          variant="body2"
          fontWeight="medium"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {data.label}
        </Typography>

        {data.description && (
          <Tooltip title={data.description}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mt: 0.5,
              }}
            >
              {data.description}
            </Typography>
          </Tooltip>
        )}

        {/* Reward info for LocationAction nodes */}
        {data.nodeType === WorkflowNodeType.LocationAction && data.locationItem && (
          <Chip
            label={data.locationItem.name}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 1, maxWidth: '100%' }}
          />
        )}

        {/* Conditions count for Decision nodes */}
        {data.nodeType === WorkflowNodeType.Decision && data.conditions.length > 0 && !hideConditionChip && (
          <Chip
            label={`${data.conditions.length} condition${data.conditions.length !== 1 ? 's' : ''}`}
            size="small"
            color="warning"
            variant="outlined"
            sx={{ mt: 1 }}
          />
        )}

        {/* Loop info */}
        {data.nodeType === WorkflowNodeType.Loop && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            {data.loopCooldownSeconds && (
              <Chip
                label={`${data.loopCooldownSeconds}s cooldown`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
            {data.maxLoops && (
              <Chip
                label={`max ${data.maxLoops}x`}
                size="small"
                color="error"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Numpad info */}
        {data.numpadConfig && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
            <Chip
              label={`${data.numpadConfig.digitCount} digits`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Box>
        )}

        {/* Question/Answer info */}
        {data.questionAnswerConfig && (
          <Box sx={{ mt: 1 }}>
            {data.questionAnswerConfig.questionText ? (
              <Tooltip title={data.questionAnswerConfig.questionText}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontStyle: 'italic',
                  }}
                >
                  "{data.questionAnswerConfig.questionText}"
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="caption" color="error" sx={{ fontStyle: 'italic' }}>
                No question set
              </Typography>
            )}
          </Box>
        )}

        {children}
      </Box>

      {/* Source Handle(s) (right) - single handle when no labels and not a cancel-handle node */}
      {showSourceHandle && sourceHandleCount === 1 && !sourceHandleLabels && !showCancelHandle && (
        <Handle
          id="flow-source"
          type="source"
          position={Position.Right}
          isConnectable={true}
          style={{
            background: borderColor,
            width: 16,
            height: 16,
            border: '2px solid white',
            zIndex: 50,
          }}
        />
      )}

      {/* Multiple source handles for Loop nodes (continue vs max reached) */}
      {showSourceHandle && sourceHandleCount === 2 && data.nodeType === WorkflowNodeType.Loop && (
        <>
          <Handle
            id="continue"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: borderColor,
              width: 16,
              height: 16,
              border: '2px solid white',
              top: '30%',
              zIndex: 50,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 20,
              top: '30%',
              transform: 'translateY(-50%)',
              fontSize: '0.6rem',
              color: 'text.secondary',
              pointerEvents: 'none',
            }}
          >
            continue
          </Typography>
          <Handle
            id="maxReached"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: '#f44336',
              width: 16,
              height: 16,
              border: '2px solid white',
              top: '70%',
              zIndex: 50,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 20,
              top: '70%',
              transform: 'translateY(-50%)',
              fontSize: '0.6rem',
              color: 'error.main',
              pointerEvents: 'none',
            }}
          >
            max
          </Typography>
        </>
      )}

      {/* Cancel handle */}
      {showSourceHandle && showCancelHandle && (
        <>
          <Handle
            id="cancel"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: '#9e9e9e',
              width: 16,
              height: 16,
              border: '2px solid white',
              top: 55,
              zIndex: 50,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 20,
              top: 55,
              transform: 'translateY(-50%)',
              fontSize: '0.55rem',
              color: 'text.secondary',
              pointerEvents: 'none',
            }}
          >
            cancel
          </Typography>
        </>
      )}

      {/* Skip handle (for Q/A nodes) */}
      {showSourceHandle && showSkipHandle && (
        <>
          <Handle
            id="skip"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: '#ff9800',
              width: 16,
              height: 16,
              border: '2px solid white',
              top: showCancelHandle ? 80 : 55,
              zIndex: 50,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 20,
              top: showCancelHandle ? 80 : 55,
              transform: 'translateY(-50%)',
              fontSize: '0.55rem',
              color: 'warning.main',
              pointerEvents: 'none',
            }}
          >
            skip
          </Typography>
        </>
      )}

      {/* Failed handle (for numpad nodes - max attempts exceeded) */}
      {showSourceHandle && showFailedHandle && (
        <>
          <Handle
            id="failed"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: '#f44336',
              width: 16,
              height: 16,
              border: '2px solid white',
              top: 80,
              zIndex: 50,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 20,
              top: 80,
              transform: 'translateY(-50%)',
              fontSize: '0.55rem',
              color: 'error.main',
              pointerEvents: 'none',
            }}
          >
            failed
          </Typography>
        </>
      )}

      {/* Q/A "Correct" handle */}
      {showSourceHandle && isQuestionAnswerNode && (
        <>
          <Handle
            id="correct"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: '#4caf50',
              width: 16,
              height: 16,
              border: '2px solid white',
              top: showSkipHandle ? 105 : (showCancelHandle ? 80 : 55),
              zIndex: 50,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 20,
              top: showSkipHandle ? 105 : (showCancelHandle ? 80 : 55),
              transform: 'translateY(-50%)',
              fontSize: '0.55rem',
              color: 'success.main',
              fontWeight: 'bold',
              pointerEvents: 'none',
            }}
          >
            correct
          </Typography>
        </>
      )}

      {/* Q/A "Incorrect" handle */}
      {showSourceHandle && showIncorrectHandle && isQuestionAnswerNode && (
        <>
          <Handle
            id="incorrect"
            type="source"
            position={Position.Right}
            isConnectable={true}
            style={{
              background: '#f44336',
              width: 16,
              height: 16,
              border: '2px solid white',
              top: showSkipHandle ? 130 : (showCancelHandle ? 105 : 80),
              zIndex: 50,
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              right: 20,
              top: showSkipHandle ? 130 : (showCancelHandle ? 105 : 80),
              transform: 'translateY(-50%)',
              fontSize: '0.55rem',
              color: 'error.main',
              fontWeight: 'bold',
              pointerEvents: 'none',
            }}
          >
            incorrect
          </Typography>
        </>
      )}

      {/* Labeled source handles without cancel handle (e.g. ModeratorApproval) */}
      {showSourceHandle && sourceHandleLabels && !showCancelHandle && !isQuestionAnswerNode && sourceHandleLabels.map((label, index) => {
        const handleColors: Record<string, string> = { Approved: '#4caf50', Denied: '#f44336', Timeout: '#ff9800' }
        const handleColor = handleColors[label] || borderColor
        const total = sourceHandleLabels.length
        const startPx = 70
        const endPx = actualHeight - 15
        const step = total > 1 ? (endPx - startPx) / (total - 1) : 0
        const positionPx = total === 1 ? actualHeight * 0.5 : startPx + (step * index)
        return (
          <Fragment key={`labeled-${index}`}>
            <Handle
              id={label.toLowerCase()}
              type="source"
              position={Position.Right}
              isConnectable={true}
              style={{
                background: handleColor,
                width: 16,
                height: 16,
                border: '2px solid white',
                top: positionPx,
                zIndex: 50,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                right: 20,
                top: positionPx,
                transform: 'translateY(-50%)',
                fontSize: '0.65rem',
                fontFamily: 'monospace',
                color: 'text.primary',
                fontWeight: 'bold',
                pointerEvents: 'none',
              }}
            >
              {label}
            </Typography>
          </Fragment>
        )
      })}

      {/* Numpad/QR code handles - dynamically rendered (not for Q/A nodes) */}
      {showSourceHandle && showCancelHandle && !isQuestionAnswerNode && sourceHandleLabels && sourceHandleLabels.map((label, index) => {
        const positionPx = getCodeHandlePosition(index, sourceHandleLabels.length, actualHeight)
        return (
          <Fragment key={`code-${index}`}>
            <Handle
              id={`code-${index}`}
              type="source"
              position={Position.Right}
              isConnectable={true}
              style={{
                background: borderColor,
                width: 16,
                height: 16,
                border: '2px solid white',
                top: positionPx,
                zIndex: 50,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                right: 20,
                top: positionPx,
                transform: 'translateY(-50%)',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                color: 'text.primary',
                fontWeight: 'bold',
                pointerEvents: 'none',
              }}
            >
              {label}
            </Typography>
          </Fragment>
        )
      })}

    </Box>
  )
}

export default memo(BaseNode)
